import React from "react";
import { AlertTriangle, Wand2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FormField } from "../UI/FormField";
import type { IntakeIssue } from "../../types/containerOrderIntake";
import { fieldIssue } from "../../utils/intakeDraft";

interface IntakeFieldProps {
  /** The draft path this field edits — also how an issue finds it. */
  path: string;
  label: string;
  issues: IntakeIssue[];
  required?: boolean;
  /** What Oliver wrote, when it differs from what is in the box. */
  source?: string | null;
  hint?: string;
  className?: string;
  /** Applies an issue's one-click fix, where it offers one. */
  onApplySuggestion?: (field: string, value: string) => void;
  readOnly?: boolean;
  children: (hasError: boolean) => React.ReactNode;
}

/**
 * One field on the review page.
 *
 * Carries `data-intake-path` so an issue can find it, shows the issues for
 * exactly this path, and — when the value was changed from what the document
 * said — the original underneath, so a reviewer never loses sight of what
 * Oliver actually read.
 *
 * Blocking and warning are shown differently on purpose: a blocking issue is
 * the control's own error, because it is the reason Accept is refused, while a
 * warning sits under the field in amber and leaves the field alone. Both are
 * shown here rather than only summarised above the form — the summary listed
 * every issue a second time, and a message belongs beside the box it is about.
 */
export const IntakeField: React.FC<IntakeFieldProps> = ({
  path,
  label,
  issues,
  required,
  source,
  hint,
  className,
  onApplySuggestion,
  readOnly,
  children,
}) => {
  const { t } = useTranslation("containerOrderIntake");
  const { blocking, warning } = fieldIssue(issues, path);
  const error = blocking?.message;
  // The blocking one owns the extras: it is the one that has to be acted on.
  const detailed = blocking ?? warning;

  return (
    <div data-intake-path={path} className={className}>
      <FormField
        label={label}
        required={required}
        error={error}
        hint={source ? `${hint ? `${hint} · ` : ""}Oliver: ${source}` : hint}
        size="sm"
      >
        {children(!!error)}
      </FormField>

      {warning && !error && (
        <div className="mt-0.5 flex items-start gap-1 text-[11px] text-amber-700">
          <AlertTriangle className="h-3 w-3 mt-[1px] shrink-0" />
          <span>{warning.message}</span>
        </div>
      )}

      {detailed?.hint && (
        <div className="mt-0.5 text-[11px] text-[#8A9590]">{detailed.hint}</div>
      )}

      {detailed?.suggestion && onApplySuggestion && !readOnly && (
        <button
          type="button"
          onClick={() =>
            onApplySuggestion(detailed.suggestion!.field, detailed.suggestion!.value)
          }
          className="mt-1 inline-flex items-center gap-1 rounded border border-[#C97A3C] px-2 py-0.5 text-[11px] text-[#C97A3C] hover:bg-[#C97A3C] hover:text-white"
        >
          <Wand2 className="h-3 w-3" />
          {t("issues.use", { value: detailed.suggestion.value })}
        </button>
      )}
    </div>
  );
};

export default IntakeField;
